import {Box, Chip, Container, Divider, Link, Typography} from "@mui/material";
import {motion, useAnimation, useInView} from "framer-motion";
import {useEffect, useRef} from "react";
import {useParams} from "react-router-dom";
import LandingPage from "../LandingPage/LandingPage";
import Grid from "@mui/material/Grid2";
import deadlocket from "../../data/deadlocket";
import DeadlocketProject from "./DeadlocketProject";

const projects = [
    deadlocket,
    {
        "id": "sea-of-marbles",
        "title": "Sea of Marbles",
        "description": "Sea of Marbles is a pirate-themed marble shooter where players aim a ship's cannon, match colors, and trigger chain reactions before rolling marbles reach the skull gate.",
        "features": [
            "Match-three marble shooting and chain reactions",
            "Campaign puzzles with three-star scoring",
            "Endless levels and continuous Arcade runs",
            "Power-ups including Rewind, Fireballs, and Tri-Shot",
            "Unlockable marbles, ships, shot trails, and sea environments"
        ],
        "technical": "Portrait mobile puzzle gameplay combining touch aiming, color swapping, campaign progression, Arcade score chasing, and collectible customizations.",
        "stack": [
            " Unity, ",
            "C#, ",
            "iOS, ",
            "Android"
        ],
        "externalLinks": [
            {
                "label": "App Store",
                "href": "https://apps.apple.com/cy/app/sea-of-marbles/id6810241959"
            },
            {
                "label": "Google Play",
                "href": "https://play.google.com/store/apps/details?id=com.bandletech.seaofmarbles"
            }
        ],
        "images": [
            "/images/bandletech/sea-of-marbles-1.jpg",
            "/images/bandletech/sea-of-marbles-2.jpg"
        ]
    },
    {
        "id": "infinity-brick-breaker",
        "title": "Infinity Brick Breaker",
        "description": "Infinity Brick Breaker is a paddle-and-ball arcade game where players control rebounds, smash bricks, and keep each run alive through endless levels.",
        "features": [
            "One-finger paddle controls",
            "Endless brick-breaking levels",
            "Falling power-ups and equippable boosters",
            "Multiball, lasers, and piercing shots",
            "Custom balls, paddles, trails, boards, and animated themes"
        ],
        "technical": "Mobile arcade gameplay built around precise paddle control, varied brick layouts, run-changing power-ups, and visual customization.",
        "stack": [
            " Unity, ",
            "C#, ",
            "iOS, ",
            "Android"
        ],
        "externalLinks": [
            {
                "label": "App Store",
                "href": "https://apps.apple.com/cy/app/infinity-brick-breaker/id6808669217"
            },
            {
                "label": "Google Play",
                "href": "https://play.google.com/store/apps/details?id=com.bandletech.brickbreaker"
            }
        ],
        "images": [
            "/images/bandletech/infinity-brick-breaker-1.jpg",
            "/images/bandletech/infinity-brick-breaker-2.jpg"
        ]
    },
    {
        id: "infinity-sort",
        title: "Infinity Sort! Color Puzzle",
        description: "Infinity Sort! Color Puzzle is a relaxing mobile sorting game where players pour matching colors between tubes, plan around empty space, and clear each board through calm logic play.",
        features: [
            "Relaxing color-sorting gameplay",
            "Simple tap-to-pour controls",
            "Hidden tubes, locks, clouds, and blockers",
            "Undo, Shuffle, and Extra Tube boosts",
            "Unlockable bottle themes and boards",
            "Endless mode",
        ],
        stack: [" Unity, ", "C#, ", "Firebase, ", "iOS, ", "Android"],
        technical: "A released Bandletech mobile game focused on clean portrait UX, readable puzzle rules, level variety, boost systems, themes, and long-term endless play.",
        externalLinks: [
            {label: "Bandletech game page", href: "https://bandletech.com/games/infinity-sort/"},
            {label: "App Store", href: "https://apps.apple.com/app/id6762474776"},
            {label: "Google Play", href: "https://play.google.com/store/apps/details?id=com.bandletech.infinitysort"},
        ],
        images: [
            "/images/bandletech/infinity-sort-1.webp",
            "/images/bandletech/infinity-sort-2.webp",
        ],
    },
    {
        id: "infinity-solitaire",
        title: "Infinity Solitaire",
        description: "Infinity Solitaire is a mobile TriPeaks solitaire game with a space theme, a large amount of content, boosters, daily rewards, and offline-friendly play.",
        features: [
            "Lots of TriPeaks-style content",
            "Power cards and boosters",
            "Fast and smooth gameplay",
            "Daily rewards",
            "Offline-friendly play",
        ],
        stack: [" Unity, ", "C#, ", "Firebase, ", "iOS, ", "Android"],
        technical: "A released Bandletech mobile card game built around smooth TriPeaks pacing, progression content, reward loops, boosters, and offline mobile sessions.",
        externalLinks: [
            {label: "Bandletech game page", href: "https://bandletech.com/games/infinity-solitaire/"},
            {label: "App Store", href: "https://apps.apple.com/gr/app/infinity-solitaire/id6746650051"},
            {label: "Google Play", href: "https://play.google.com/store/apps/details?id=com.bandletech.infinitysolitaire"},
        ],
        images: [
            "/images/bandletech/infinity-solitaire-1.webp",
            "/images/bandletech/infinity-solitaire-2.webp",
        ],
    },
    {
        id: "match-magic-rpg",
        title: "Match Magic RPG",
        description: "Match Magic RPG is a mobile puzzle RPG built around match-based combat, progression systems, quests, challenge modes, boss encounters, a leaderboard, and 99 achievements.",
        features: [
            "Match-based combat feel",
            "Unlockable heroes, spells, and items",
            "Boss encounters",
            "Quests and challenge modes",
            "Leaderboard competition",
            "99 achievements",
            "Quick portrait sessions with deeper progression",
        ],
        stack: [" Unity, ", "C#, ", "Firebase, ", "iOS, ", "Android"],
        technical: "A released Bandletech mobile puzzle RPG combining portrait-friendly match play with heroes, spells, items, quests, challenge modes, boss fights, achievements, and leaderboard competition.",
        externalLinks: [
            {label: "Bandletech game page", href: "https://bandletech.com/games/match-magic-rpg/"},
            {label: "App Store", href: "https://apps.apple.com/gr/app/match-magic-rpg/id6756521036"},
            {label: "Google Play", href: "https://play.google.com/store/apps/details?id=com.bandletech.infinitymatchrpg"},
        ],
        images: [
            "/images/bandletech/match-magic-rpg-1.webp",
            "/images/bandletech/match-magic-rpg-2.webp",
        ],
    },
    {
        id: "apocalypticsurvival",
        title: " Apocalyptic Survival",
        description: 'Apocalyptic Survival is a roguelike zombie survival game currently in development.\n' +
            '\n' +
            'The game features different ways to kill zombies, and different types of weapons.\n' +
            'As the player progresses, the game will become more difficult to play, and new types of zombies will start appearing.\n' +
            'A new type of zombie will appear every time the player kills a certain number of zombies.\n' +
            '\n' +
            'A small endless demo of the game can be played on the browser by clicking the link below.',
        features: [
            'High Score',
            'Guns',
            'Powerups',
            'Lots of Zombies',
        ],
        stack: [' Unity, ', 'C#, ', 'Javascript'],
        technical: 'This project showcases my ability to create functional web games through Unity',
        link: 'https://balzor.github.io/TheZombieGame/',
        images: [
            // '/images/spellthiefmatch/1.png',
            // '/images/spellthiefmatch/2.png',
            // '/images/spellthiefmatch/3.png',
            // '/images/spellthiefmatch/4.png',
            // '/images/spellthiefmatch/5.png',
            // '/images/spellthiefmatch/6.png',
            // '/images/spellthiefmatch/7.png',
            // '/images/spellthiefmatch/8.png',
        ],
    },
    {
        id: "driftuniverse",
        title: " Drift Universe",
        description: 'Drift Universe is an Endless Driving Game where you collect cars and drive everywhere.\n' +
            '\n' +
            'Choose your car, choose your universe and hit the road.\n' +
            'Drift through different Universes and different Timelines, with new places added frequently.\n' +
            '\n' +
            'Put the pedal to the metal, try not to crash and most importantly don\'t forget to DRIFT.',
        features: [
            'Infinite Drifting',
            'Multiple Universes',
            'Lots of Cars',
            'Customizations',
            'Car Tuning',
            'Missions'
        ],
        stack: [' Unity, ', 'C#'],
        technical: 'This project showcases my understanding of physics in Unity engine, the process of procedurally generated levels to create an endless runner and the combination of fun and engaging gameplay.',
        link: '',
        images: [
            '/images/driftIcon.png',
            '/images/Screenshot 1.png',
            '/images/Screenshot 3.png',
            '/images/Screenshot 4.png',
        ],
    },
    {
        id: "apocalypserun",
        title: " Apocalypse Run",
        description: 'Can you survive long enough to help your settlement?\n' +
            '\n' +
            'You finally found a settlement in an apocalyptic world. Are you brave enough to outrun the hordes of zombies to help other survivors? Swipe to turn, jump and slide to avoid obstacles. Test you reflexes as you run in abandoned cities, collecting resources to help your camp.\n' +
            '\n' +
            'Recruit new survivors to help you along the way.\n' +
            '\n' +
            'Unlock crazy powerups to aid you in each run.\n' +
            '\n' +
            'Climb the leaderboard and show to everyone that you are the true survivor.',
        features: [
            'Daily Missions',
            'Unlockable Characters',
            'Crazy Powerups',
            'High Score',
            'Endless Mode',
        ],
        stack: [' Unity, ', 'C#'],
        technical: 'This project showcases my understanding of creating a mission based game in combination with an endless runner to create an engaging experience.',
        link: '',
        images: [
            '/images/apocalypse1.jpg',
            '/images/apocalypse2.jpg',
            '/images/apocalypse3.jpg',
            '/images/apocalypse4.jpg',
            '/images/apocalypse5.jpg',
            '/images/apocalypse6.jpg',
        ],
    },
    {
        id: "unitydex",
        title: "Unity Pokedex",
        description: 'UnityDex is a fully functional Pokedex that includes detailed data on every single Pokémon. Includes full entries, types, abilities, and so more!',
        features: [
            'All Pokemons',
            'Detailed Descriptions of Pokemon',
            'Regional Forms',
            'Shiny Forms',
            'Type Dex',
            'Ability Dex',
            'Nature Dex',
            'Search Functionality',
        ],
        stack: [' Unity, ', 'C#, ','Javascript'],
        technical: 'This project showcases my ability to process and handle large amounts of data, in this case over 900 entries of Pokemon.',
        link: 'https://balzor.github.io/Pokedex/',
        images: [
            '/images/pokemon screenshot 1.jpg',
            '/images/pokemon screenshot 2.jpg',
            '/images/pokemon screenshot 3.jpg',
            '/images/pokemon screenshot 4.jpg',
            '/images/pokemon screenshot 5.jpg',
            '/images/pokemon screenshot 6.jpg',
        ]
    },
    {
        id: "flap",
        title: "Flappy Duckz",
        description: 'FLAP.',
        features: [
            'QUACK.',
        ],
        stack: [' Unity, ', 'C#, ','Javascript'],
        technical: 'FLAP',
        link: 'https://balzor.github.io/FlappyDuckz/',
        images: [
            '/images/flappyDuckzIcon.jpg',
            '/images/flappy1.png',
            '/images/flappy2.png',
        ]
    },
    {
        id: "tictactoe",
        title: "Tic Tac Toe",
        description: 'A simple Tic Tac Toe Game.',
        features: [
            'Difficulty Modes',
            'Score Tracking',
        ],
        stack: [' Unity, ', 'C#'],
        technical: 'A project to create an impossible bot in tic tac toe',
        link: '',
        images: [
            '/images/tictactoe1.png',
            '/images/tictactoe2.png',
        ]
    },
];

const ProjectSubPage = () => {
    const {id} = useParams();
    const item = projects.find(project => project.id === id);


    const ref = useRef(null);
    const controls = useAnimation();
    const isInView = useInView(ref, {once: false, amount: 0.2}); // Detects if it's in view

    useEffect(() => {
        if (isInView) {
            controls.start({opacity: 1, y: 0});
        }
    }, [isInView, controls]); // Runs when the view state changes

    if (!item) {
        return <LandingPage/>;
    }

    if (item.id === deadlocket.id) {
        return <DeadlocketProject/>;
    }

    return (
        <>
            <Container>
                <Box sx={{
                    paddingTop: '20px',
                    paddingBottom: '40px',
                    // py: 5,
                    px: {xs: 3, md: 6},
                    textAlign: "left",
                    color: "white"
                }}>
                    <motion.div
                        ref={ref}
                        transition={{duration: 0.3}}
                        initial={{opacity: 0, y: 50}}
                        animate={controls} // Now controlled by `useAnimation`
                        whileInView={{opacity: 1, y: 0}}
                    >
                        <Box sx={{display: "flex", justifyContent: "space-between"}}>
                            <Box sx={{height: '100%', alignItems: 'center'}}>
                                {/* Title & Underline */}
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: "bold",
                                        textTransform: "uppercase",
                                        fontFamily: `"Poppins", sans-serif`,
                                        display: "inline-block",
                                        fontSize: {xs: '14px', md: '24px'},
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                <Divider
                                    sx={{
                                        backgroundColor: "#FFD700", // Faint Gold Underline
                                        height: "2px",
                                        width: "80px",
                                        mt: 1,
                                        mb: 4,
                                        // mx: "auto",
                                    }}
                                />
                            </Box>
                        </Box>
                        <Box>
                            <ul style={{
                                listStyle: 'none',
                                paddingLeft: '0px',
                                gap: '10px',
                            }}>
                                <li style={{paddingBottom: '10px'}}>
                                    <Chip color="success" label="Description"/> {item.description}
                                </li>
                                <li style={{paddingBottom: '10px'}}>
                                    <Chip color="success" label="Features"/>
                                    <ul>
                                        {item.features.map((module, index) => (
                                            <li key={index}>
                                                <p>
                                                    {module}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                                <li style={{paddingBottom: '10px'}}>
                                    <Chip color="success" label="Stack"/>
                                    {item.stack.map((module, index) => (
                                        <span key={index}>
                                            {module}
                                        </span>
                                    ))}
                                </li>
                                <li style={{paddingBottom: '10px'}}>
                                    {item.technical}
                                </li>
                                {item.link && (
                                    <li style={{paddingBottom: '10px'}}>
                                        <Link href={item.link} target="_blank" rel="noreferrer"> Check it out here</Link>
                                    </li>
                                )}
                                {item.externalLinks && (
                                    <li style={{paddingBottom: '10px'}}>
                                        <Chip color="success" label="Links"/>
                                        <ul>
                                            {item.externalLinks.map((externalLink, index) => (
                                                <li key={index}>
                                                    <p>
                                                        <Link href={externalLink.href} target="_blank" rel="noreferrer">
                                                            {externalLink.label}
                                                        </Link>
                                                    </p>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                )}
                            </ul>
                        </Box>
                        <Grid container spacing={2} justifyContent="center">
                            {item.images.map((project, index) => (
                                <Grid key={index} size={{lg: 3, md: 3, sm: 6, xs: 6}}>
                                    <img style={{
                                        width: '100%',
                                        borderRadius: '10px'
                                    }} src={project} alt={project}/>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>
                </Box>
            </Container>
        </>
    )
}

export default ProjectSubPage;
